import {Dispatch, SetStateAction, useEffect, useState} from 'react';

import {useLoading} from '@/app/zustand/useLoading';
import {
  REFERRAL_CODE_STATUS,
  SIGN_UP_CODE_TYPE,
} from '@/dataSource/src/api/users/signUpCodeConstants';
import {useUsersRepository} from '@/dataSource/src/api/users/useRepository';

const SIGN_UP_CODE_DEBOUNCE_MS = 1000;

interface UseSignUpCodeValidationParams {
  signUpCode: string;
  setInvitationCode: Dispatch<SetStateAction<string>>;
  setReferralCode: Dispatch<SetStateAction<string>>;
}

interface UseSignUpCodeValidationResult {
  isValidSignUpCode: boolean;
  isValidatingCode: boolean;
  invitationCodeWritten: boolean;
}

// Debounces the invitation/referral code the user types and validates it
// against the API, routing it into invitationCode or referralCode depending
// on what the API resolves it to.
export function useSignUpCodeValidation({
  signUpCode,
  setInvitationCode,
  setReferralCode,
}: UseSignUpCodeValidationParams): UseSignUpCodeValidationResult {
  const [isValidSignUpCode, setIsValidSignUpCode] = useState<boolean>(true);
  const [isValidatingCode, setIsValidatingCode] = useState<boolean>(false);
  const [invitationCodeWritten, setInvitationCodeWritten] =
    useState<boolean>(false);
  const {triggerVerifySignUpCode} = useUsersRepository();
  const {setSkipLoading} = useLoading();

  useEffect(() => {
    // Guards against two issues with in-flight requests: (1) an error
    // response left isValidatingCode stuck at true forever, permanently
    // blocking step 1; (2) a stale response from an earlier keystroke
    // resolving after a newer one and clobbering the current state.
    let active = true;
    let debounceTimer: NodeJS.Timeout;

    if (signUpCode?.trim() === '') {
      setReferralCode('');
      setIsValidSignUpCode(true);
      setIsValidatingCode(false);
      setInvitationCodeWritten(false);
      return;
    }

    setIsValidatingCode(true);
    setSkipLoading(true);

    // eslint-disable-next-line prefer-const
    debounceTimer = setTimeout(async () => {
      try {
        const response = await triggerVerifySignUpCode(signUpCode).unwrap();
        if (!active) return;

        setIsValidatingCode(false);
        setInvitationCodeWritten(true);

        if (!response) return;

        if (response.code_type === SIGN_UP_CODE_TYPE.INVITATION_CODE) {
          setInvitationCode(signUpCode);
          setReferralCode('');
          setIsValidSignUpCode(true);
          setInvitationCodeWritten(true);
        } else if (response.code_type === SIGN_UP_CODE_TYPE.REFERRAL_CODE) {
          setReferralCode(signUpCode);
          if (response.referral?.status !== REFERRAL_CODE_STATUS.ACTIVE) {
            setIsValidSignUpCode(false);
          } else {
            setIsValidSignUpCode(true);
          }
        } else {
          setIsValidSignUpCode(false);
        }
      } catch {
        if (!active) return;
        setIsValidatingCode(false);
        setIsValidSignUpCode(false);
      }
    }, SIGN_UP_CODE_DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(debounceTimer);
    };
  }, [
    setInvitationCode,
    setReferralCode,
    setSkipLoading,
    signUpCode,
    triggerVerifySignUpCode,
  ]);

  return {isValidSignUpCode, isValidatingCode, invitationCodeWritten};
}
